import { useAppState } from "../../context/appStateContext"
import { PaywallItem, usePurchase } from "../../context/purchaseContext"
import { formatCurrency } from "../../utils/utils"
import images from "../../utils/images"
import PrimaryButton from "../PrimaryButton/PrimaryButton"
import Switch from "react-ios-switch"
import styles from "./PaywallListFooter.module.scss"
import useStrings from "../../localization/useStrings"

const PaywallListFooter = () => {
  const bottomInset = useAppState().state.initialData?.bottomInset ?? 0
  const { state, actions } = usePurchase()
  const strings = useStrings()

  const ctaTitle = state.trialFooterEnabled
    ? state.yearlyItem.selected
      ? strings.trialFooter.cta
      : strings.subscribe
    : state.ctaTitle

  const selectedProduct = state.selectedProduct
  const trialDescription = selectedProduct.hasTrial
    ? selectedProduct.duration === "yearly"
      ? strings.freeTrialDescriptionYearly(
          state.trialDays,
          formatCurrency(selectedProduct.price, selectedProduct.priceLocale),
        )
      : strings.freeTrialDescriptionMonthly(
          state.trialDays,
          formatCurrency(selectedProduct.price, selectedProduct.priceLocale),
        )
    : null

  return (
    <div className={styles.container}>
      <div className={styles.background} style={{ bottom: -bottomInset }}></div>
      {state.toggleableFreeTrial && <ToggleTrial />}
      <div className={styles.selection}>
        <PaywallListItem
          item={state.yearlyItem}
          onClick={() => actions.setYearlySelected(true)}
        />
        <PaywallListItem
          item={state.monthlyItem}
          onClick={() => actions.setYearlySelected(false)}
        />
      </div>
      {state.trialFooterEnabled && (
        <div className={styles.noCommitment}>
          <span className={styles.checkmark}>&#10003;</span>
          {strings.trialFooter.noCommitment}
        </div>
      )}
      <div className={styles.ctaContainer}>
        <PrimaryButton onClick={() => actions.purchaseCurrent()}>
          {ctaTitle}
        </PrimaryButton>
      </div>
      {trialDescription && (
        <div className={styles.trialDescription}>{trialDescription}</div>
      )}
    </div>
  )
}

interface PaywallListItemProps {
  item: PaywallItem
  onClick: () => void
}

const PaywallListItem = (props: PaywallListItemProps) => {
  const {
    toggleableFreeTrial,
    freeTrialToggled,
    trialFooterEnabled,
    trialDays,
    selectedPlan,
  } = usePurchase().state
  const strings = useStrings()

  const item = props.item
  const selected = item.selected

  const paywallImages = images.premium.default

  const checkBoxImage = selected
    ? paywallImages.checkBox
    : paywallImages.checkBoxUnchecked
  const selectionStyle = selected ? styles.selected : ""

  if (trialFooterEnabled) {
    const isYearly = item.yearlyPrice != null
    const showBadge = isYearly
    const trialText = isYearly
      ? strings.trialFooter.freeTrialDays(trialDays)
      : strings.trialFooter.noFreeTrial
    const locale = selectedPlan.yearlyProduct.priceLocale
    const subtitle = isYearly
      ? `${formatCurrency(selectedPlan.yearlyProduct.price, locale)}/year (${formatCurrency(selectedPlan.yearlyProduct.price / 12, locale)}/mo)`
      : `${formatCurrency(selectedPlan.monthlyProduct.price, locale)}/mo`

    return (
      <button
        className={`${styles.pricing} ${selectionStyle}`}
        onClick={props.onClick}
      >
        {showBadge && <div className={styles.discount}>{strings.popular}</div>}
        <div className={styles.leftPricing}>
          <span className={styles.time}>
            {isYearly
              ? strings.trialFooter.yearly
              : strings.trialFooter.monthly}
          </span>
          <span className={styles.perYear}>{subtitle}</span>
        </div>
        <div className={styles.rightPricing}>
          <span className={styles.trialText}>{trialText}</span>
          <div className={`${checkBoxImage.class} ${styles.checkbox}`} />
        </div>
      </button>
    )
  }

  let discountTitle = item.discount

  if (toggleableFreeTrial && discountTitle != null) {
    discountTitle = freeTrialToggled
      ? strings.trialActivated(7)
      : strings.popular
  }

  return (
    <button
      className={`${styles.pricing} ${selectionStyle}`}
      onClick={props.onClick}
    >
      {item.discount && <div className={styles.discount}>{discountTitle}</div>}
      <div className={styles.leftPricing}>
        <span className={styles.time}>{item.title}</span>
        {item.yearlyPrice && (
          <span className={styles.perYear}>{item.yearlyPrice}</span>
        )}
      </div>
      <div className={styles.rightPricing}>
        <span className={styles.price}>{item.monthlyPrice}</span>
        <div className={`${checkBoxImage.class} ${styles.checkbox}`} />
      </div>
    </button>
  )
}

const ToggleTrial = () => {
  const { state, actions } = usePurchase()

  const root = document.documentElement
  const primaryColor = getComputedStyle(root)
    .getPropertyValue("--color-primary")
    .trim()
  const offColor = getComputedStyle(root)
    .getPropertyValue("--pricing-selected-stroke")
    .trim()
  const strings = useStrings()

  return (
    <div className={styles.toggleContainer}>
      <label className={styles.toggleTitle}>{strings.enableTrial}</label>
      <Switch
        checked={state.freeTrialToggled}
        onChange={actions.setFreeTrialToggled}
        offColor={offColor}
        onColor={primaryColor}
      />
    </div>
  )
}

export default PaywallListFooter
